// ==========================================================================
// PAYROLL CALCULATION ENGINE & UTILITIES
// WuRuPayroll - Core calculation logic, tax/SS rules & Thai Baht formatting
// ==========================================================================


// Helper to resolve calendar events from parameter or global state safely
function getCalendarEvents(calendarState) {
  if (calendarState && (calendarState.holidays || calendarState.specialWorkdays)) {
    return calendarState;
  }
  if (typeof window !== 'undefined' && window.state) {
    return window.state;
  }
  if (typeof globalThis !== 'undefined' && globalThis.state) {
    return globalThis.state;
  }
  return { holidays: [], specialWorkdays: [] };
}

/**
 * Get configured daily work hours string for employee/company
 * @param {Object} emp - Employee object
 * @param {Object} companySettings - Company settings
 * @param {string} dayName - Day of week (sunday, monday, ...)
 * @returns {string} Configured hours string, e.g. "08:00-17:00" or "8"
 */
export function getEmployeeWorkHoursStr(emp, companySettings, dayName) {
  if (emp && emp.useCustomWorkHours && emp.customWorkHours && emp.customWorkHours[dayName]) {
    return emp.customWorkHours[dayName];
  }
  if (!companySettings || !companySettings.workHoursPerDay) return '8';
  return companySettings.workHoursPerDay[dayName] || '8';
}

const getHoursStr = (emp, settings, day) => {
  if (typeof window !== 'undefined' && window.getEmployeeWorkHoursStr) {
    return window.getEmployeeWorkHoursStr(emp, settings, day);
  }
  return getEmployeeWorkHoursStr(emp, settings, day);
};

export function isEmployeeActiveInMonth(employee, monthStr) {
  if (!employee) return false;

  // Check start date: must start in or before the target month
  if (employee.startDate) {
    const startMonth = employee.startDate.slice(0, 7);
    if (startMonth > monthStr) return false;
  }

  // Check resignation date: if resigned, must resign in or after the target month
  if (employee.status === 'resigned' && (employee.resignedDate || employee.resignDate)) {
    const resignMonth = (employee.resignedDate || employee.resignDate).slice(0, 7);
    if (resignMonth < monthStr) return false;
  } else if (employee.status === 'resigned') {
    // Resigned but no date recorded, fallback to exclude
    return false;
  }

  return true;
}

// Helper to get Withholding Tax and Social Security settings for an employee with legacy fallback
export function getEmployeeDeductionSettings(employee, companySettings) {
  if (!employee) {
    return {
      withholdingTax: { enabled: true, rate: companySettings?.withholdingTaxRate || 3 },
      socialSecurity: { enabled: true, rate: companySettings?.socialSecurityRate || 5 }
    };
  }

  let withholdingTax = employee.withholdingTax;
  let socialSecurity = employee.socialSecurity;

  if (!withholdingTax) {
    // Legacy fallback
    const isTaxEnabled = employee.isIntern || employee.type === 'freelance';
    withholdingTax = {
      enabled: isTaxEnabled,
      rate: companySettings?.withholdingTaxRate || 3,
      isCustom: false
    };
  } else if (!withholdingTax.isCustom) {
    // Dynamically resolve default from company settings!
    withholdingTax.rate = companySettings?.withholdingTaxRate || 3;
  }

  if (!socialSecurity) {
    // Legacy fallback
    const isSSEnabled = !employee.isIntern && employee.type !== 'freelance';
    socialSecurity = {
      enabled: isSSEnabled,
      rate: companySettings?.socialSecurityRate || 5,
      isCustom: false
    };
  } else if (!socialSecurity.isCustom) {
    // Dynamically resolve default from company settings!
    socialSecurity.rate = companySettings?.socialSecurityRate || 5;
  }

  return { withholdingTax, socialSecurity };
}

// Helper to parse dynamic work hours input (e.g. "07:30-19:30" or "8") to decimal hours
export function parseWorkHoursRange(rangeStr) {
  if (rangeStr === undefined || rangeStr === null) return 8;
  let str = String(rangeStr).trim();
  if (str.startsWith('OFF:')) {
    str = str.substring(4);
  }
  if (!str || str === '0') return 0;

  // If it's a plain number, return it directly
  if (!isNaN(str)) {
    return parseFloat(str);
  }

  // Parse time range, e.g., "7:30-19:30" or "07:30 - 19:30น."
  const parts = str.split(/[-~]|to/i);
  if (parts.length === 2) {
    const parseTime = (tStr) => {
      const clean = tStr.replace(/[^0-9:]/g, '').trim();
      const timeParts = clean.split(':');
      if (timeParts.length >= 1) {
        const hh = parseInt(timeParts[0]) || 0;
        const mm = parseInt(timeParts[1]) || 0;
        return hh + (mm / 60);
      }
      return 0;
    };

    const start = parseTime(parts[0]);
    const end = parseTime(parts[1]);

    if (start > 0 || end > 0) {
      if (end >= start) {
        return end - start;
      } else {
        // Overnight shift (e.g., 22:00 to 06:00)
        return (24 - start) + end;
      }
    }
  }

  const parsed = parseFloat(str);
  return isNaN(parsed) ? 8 : parsed;
}

// ==========================================================================
// PAYROLL CALCULATION ENGINE (Requirement 5, 6, 7, 8, 9, 10, 26)
// ==========================================================================
export function calculatePayrollForEmployee(employee, timesheet, companySettings, calendarState = null) {
  const state = getCalendarEvents(calendarState);
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const currentMonthStr = `${currentYear}-${String(currentMonth).padStart(2, '0')}`;
  const todayDate = now.getDate();
  const todayString = `${currentMonthStr}-${String(todayDate).padStart(2, '0')}`;
  const [yearStr, monthStr] = timesheet.month.split('-');
  const year = parseInt(yearStr);
  const month = parseInt(monthStr);
  const daysInMonth = new Date(year, month, 0).getDate();

  const isPastMonth = timesheet.month < currentMonthStr;
  const isCurrentMonth = timesheet.month === currentMonthStr;
  const isFutureMonth = timesheet.month > currentMonthStr;
  const isOngoingMonth = (isCurrentMonth && todayDate < daysInMonth) || isFutureMonth;

  // If timesheet is frozen/locked, or is a past month with a saved summary, use the cached summary directly!
  // This guarantees that locked records and historical months are 100% frozen and NEVER recalculated or touched.
  if (timesheet.isLocked && timesheet.summary && timesheet.summary.netSalary > 0) {
    return timesheet.summary;
  }
  if (isPastMonth && timesheet.summary && timesheet.summary.netSalary > 0) {
    return timesheet.summary;
  }

  // Apply Overrides if present (Requirement: Historical Data Overrides)
  const effectiveSalaryRate = timesheet.overrides && timesheet.overrides.salaryRate !== undefined ? parseFloat(timesheet.overrides.salaryRate) : parseFloat(employee.salaryRate || 0);
  const overrideSSRate = timesheet.overrides && timesheet.overrides.socialSecurityRate !== undefined ? parseFloat(timesheet.overrides.socialSecurityRate) : null;
  const overrideTaxRate = timesheet.overrides && timesheet.overrides.withholdingTaxRate !== undefined ? parseFloat(timesheet.overrides.withholdingTaxRate) : null;

  // Basic wage variables
  let basicSalaryEarned = 0;
  let attendanceDaysCount = 0;
  let absenceDaysCount = 0;
  let sickLeaveCount = 0;
  let annualLeaveCount = 0;
  let personalLeaveCount = 0;
  let holidaySelectedCount = 0;
  let notStartedCount = 0;
  let unpaidNotStartedCount = 0;
  let resignedCount = 0;
  let lateMinutesTotal = 0;
  let totalAdvance = 0;

  // Track status patterns per day
  const processedDays = {};
  let hasIncompleteWorkDay = false;

  for (let d = 1; d <= daysInMonth; d++) {
    let dayData = timesheet.days ? timesheet.days[d] : null;
    const dateString = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const dateObj = new Date(year, month - 1, d);

    // Detect default status based on calendars & start/resign dates
    let detectedStatus = 'work';
    const dayOfWeekNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayOfWeekNames[dateObj.getDay()];
    let isDefaultDayOff = false;
    if (employee.weeklyDaysOff !== undefined && employee.weeklyDaysOff.length > 0) {
      isDefaultDayOff = employee.weeklyDaysOff.includes(dateObj.getDay());
    } else {
      const workHoursStr = getHoursStr(employee, companySettings, dayName);
      isDefaultDayOff = workHoursStr === '0' || workHoursStr.startsWith('OFF:');
    }
    const isCompanyHoliday = state.holidays && state.holidays.some(h => h.date === dateString);
    const isSpecialWorkday = state.specialWorkdays && state.specialWorkdays.some(s => s.date === dateString);
    const isCompensatoryHoliday = state.specialWorkdays && state.specialWorkdays.some(s => s.compensatoryOffDate === dateString);

    if (employee.startDate && dateString < employee.startDate) detectedStatus = 'not_started';
    else if (employee.resignDate && dateString > employee.resignDate) detectedStatus = 'resigned';
    else if (isSpecialWorkday) detectedStatus = 'special_workday';
    else if (isCompensatoryHoliday) detectedStatus = 'compensatory_holiday';
    else if (isCompanyHoliday) detectedStatus = 'holiday';
    else if (isDefaultDayOff) detectedStatus = 'day_off';

    const isUntouched = timesheet.isPhantom && (!dayData || (!dayData.timeIn && !dayData.timeOut && !dayData.advance && (dayData.overrideAmount === undefined || dayData.overrideAmount === null || dayData.overrideAmount === '') && !dayData.remark && (!dayData.lateMinutes || parseInt(dayData.lateMinutes) === 0)));

    if (!dayData) {
      dayData = { status: (dateString > todayString ? 'not_started' : detectedStatus), advance: 0 };
    } else if (!timesheet.isLocked) {
      if (dateString <= todayString) {
        if (isUntouched && (dayData.status === 'not_started' || (dayData.status === 'work' && detectedStatus !== 'work') || timesheet.isPhantom)) {
          dayData = { ...dayData, status: detectedStatus };
        }
      } else {
        if (isUntouched && dayData.status !== 'not_started' && dayData.status !== 'resigned') {
          dayData = { ...dayData, status: 'not_started' };
        }
      }
    }

    processedDays[d] = dayData;

    // Sum advances logged on specific days
    totalAdvance += parseFloat(dayData.advance || 0);

    if (dayData.status === 'work') {
      attendanceDaysCount++;

      // Read explicit late minutes from input (do not calculate from timeIn/Out anymore based on user feedback)
      let lateMins = parseInt(dayData.lateMinutes || 0);

      lateMinutesTotal += lateMins;

      // But we still auto-calculate missing minutes (late or early leave) solely to disqualify diligence allowance (Requirement 7)
      const dayWorkHoursStr = getHoursStr(employee, companySettings, dayName);
      if (dayWorkHoursStr && dayWorkHoursStr.includes('-') && dayData.timeIn && dayData.timeOut) {
        let cleanStr = dayWorkHoursStr;
        if (cleanStr.startsWith('OFF:')) cleanStr = cleanStr.substring(4);
        const [startStr, endStr] = cleanStr.split('-');

        const [inH, inM] = dayData.timeIn.split(':').map(Number);
        const [startH, startM] = startStr.split(':').map(Number);
        const [outH, outM] = dayData.timeOut.split(':').map(Number);
        const [endH, endM] = endStr.split(':').map(Number);

        let missingMins = 0;
        if (!isNaN(inH) && !isNaN(inM) && !isNaN(startH) && !isNaN(startM)) {
          const inTotal = inH * 60 + inM;
          const startTotal = startH * 60 + startM;
          if (inTotal > startTotal) missingMins += (inTotal - startTotal);
        }
        if (!isNaN(outH) && !isNaN(outM) && !isNaN(endH) && !isNaN(endM)) {
          const outTotal = outH * 60 + outM;
          const endTotal = endH * 60 + endM;
          if (outTotal < endTotal) missingMins += (endTotal - outTotal);
        }

        if (missingMins > 0) {
          hasIncompleteWorkDay = true;
        }
      }

    } else if (dayData.status === 'absence') {
      absenceDaysCount++;
    } else if (dayData.status === 'not_started') {
      notStartedCount++;
      if (employee.startDate && dateString < employee.startDate) {
        unpaidNotStartedCount++;
      }
    } else if (dayData.status === 'resigned') {
      resignedCount++;
    } else if (dayData.status === 'leave_sick') {
      sickLeaveCount++;
    } else if (dayData.status === 'leave_annual') {
      annualLeaveCount++;
    } else if (dayData.status === 'leave_personal') {
      personalLeaveCount++;
    } else if (dayData.status === 'holiday_selected') {
      holidaySelectedCount++;
    }
  }

  // Calculate gross base wages based on employee type
  if (employee.type === 'salary') {
    const dailyRate = effectiveSalaryRate / 30; // Always divide by 30 days

    // For fixed salary employees:
    // Basic base is full monthly rate minus deductions for any absence (unpaid day)
    basicSalaryEarned = effectiveSalaryRate - ((absenceDaysCount + unpaidNotStartedCount + resignedCount) * dailyRate);

    // Apply leaves checking rules (Sick & Annual Leave settings toggle) (Requirement 27)
    if (!companySettings.enableSickLeaveCalc && sickLeaveCount > 0) {
      basicSalaryEarned -= (sickLeaveCount * dailyRate);
    }
    if (!companySettings.enableAnnualLeaveCalc && annualLeaveCount > 0) {
      basicSalaryEarned -= (annualLeaveCount * dailyRate);
    }
    if (!companySettings.enablePersonalLeaveCalc && personalLeaveCount > 0) {
      basicSalaryEarned -= (personalLeaveCount * dailyRate);
    }
    if (basicSalaryEarned < 0) basicSalaryEarned = 0;

  } else if (employee.type === 'daily' || employee.type === 'freelance') {
    // Calculate day by day to support daily override amounts
    for (let d = 1; d <= daysInMonth; d++) {
      const dayData = processedDays[d] || {};
      let isPaid = false;

      if (dayData.status === 'work' || dayData.status === 'holiday_selected' || dayData.status === 'special_workday' || dayData.status === 'holiday') {
        isPaid = true;
      } else if (employee.type === 'daily') {
        if (companySettings.enableSickLeaveCalc && dayData.status === 'leave_sick') isPaid = true;
        if (companySettings.enableAnnualLeaveCalc && dayData.status === 'leave_annual') isPaid = true;
      }

      if (isPaid) {
        if (dayData.overrideAmount !== undefined && dayData.overrideAmount !== null && dayData.overrideAmount !== '') {
          basicSalaryEarned += parseFloat(dayData.overrideAmount);
        } else {
          basicSalaryEarned += effectiveSalaryRate;
        }
      }
    }
  }

  // 1. LATE DEDUCTION CALCULATION (Requirement 8)
  // Deduct minute-by-minute based on daily hours setting in Company Settings
  let lateDeduction = 0;
  if (employee.type !== 'freelance' && lateMinutesTotal > 0) {
    // Loop through each day of the month to calculate late fees based on hours scheduled for that specific weekday
    for (let d = 1; d <= daysInMonth; d++) {
      const dayData = processedDays[d] || {};
      if (dayData.status === 'work') {
        // Find day of week name
        const dateObj = new Date(year, month - 1, d);
        const dayOfWeekNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const dayName = dayOfWeekNames[dateObj.getDay()];

        // Get work hours for this specific day from settings
        const workHoursStr = getHoursStr(employee, companySettings, dayName);
        let workHours = parseWorkHoursRange(workHoursStr);

        // Fallback checks:
        // 1. Check if this date is registered as a "Special Workday" in the calendar, and use its custom hours if defined.
        const dateString = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        const specialDay = state.specialWorkdays && state.specialWorkdays.find(s => s.date === dateString);

        if (workHours === 0) {
          // 2. Otherwise, if it's 0 (Day Off) but they worked, fallback to the first active weekday standard hours, or 8.
          for (const fallbackDay of dayOfWeekNames) {
            const fallbackStr = getHoursStr(employee, companySettings, fallbackDay);
            const fallbackHours = parseWorkHoursRange(fallbackStr);
            if (fallbackHours > 0) {
              workHours = fallbackHours;
              break;
            }
          }
          if (workHours === 0) {
            workHours = 8;
          }
        }

        if (workHours > 0) {
          // Minute rate = (DayRate / WorkHours / 60)
          const dailyRate = employee.type === 'salary' ? (effectiveSalaryRate / 30) : effectiveSalaryRate;
          const minuteRate = dailyRate / workHours / 60;

          // Use the explicitly entered lateMins for this day
          let dayLateMins = parseInt(dayData.lateMinutes || 0);

          lateDeduction += dayLateMins * minuteRate;
        }
      }
    }
  }
  // Round to 2 decimal places
  lateDeduction = Math.round(lateDeduction * 100) / 100;

  // 2. DILIGENCE ALLOWANCE (Requirement 7)
  // Diligence allowance is given only if the employee meets the company's diligence criteria:
  let diligenceAllowance = 0;

  // Resolve employee diligence settings with fallback to default company settings
  let isDiligenceEnabledForEmployee = true;
  let employeeDiligenceAmount = companySettings.diligenceAllowanceAmount !== undefined ? parseFloat(companySettings.diligenceAllowanceAmount) : 1000;

  if (employee.diligenceAllowance) {
    isDiligenceEnabledForEmployee = employee.diligenceAllowance.enabled !== false;
    if (isDiligenceEnabledForEmployee) {
      if (employee.diligenceAllowance.isCustom) {
        employeeDiligenceAmount = parseFloat(employee.diligenceAllowance.amount || 0);
      } else {
        employeeDiligenceAmount = companySettings.diligenceAllowanceAmount !== undefined ? parseFloat(companySettings.diligenceAllowanceAmount) : 1000;
      }
    }
  } else {
    // Legacy support: check if there's a recurring item named 'เบี้ยขยัน'
    const diligenceRewardItem = employee.recurringItems?.find(item => item.name === 'เบี้ยขยัน');
    if (diligenceRewardItem) {
      employeeDiligenceAmount = parseFloat(diligenceRewardItem.amount || 0);
    }
  }

  if (companySettings.enableDiligenceAllowance !== false && isDiligenceEnabledForEmployee) {
    // Retrieve criteria settings with defaults for backward compatibility
    const reqNoLate = companySettings.diligenceRequireNoLate !== false; // default true
    const reqNoAbsence = companySettings.diligenceRequireNoAbsence !== false; // default true
    const reqNoSick = companySettings.diligenceRequireNoSickLeave !== false; // default true
    const reqNoAnnual = companySettings.diligenceRequireNoAnnualLeave !== false; // default true
    const reqNoPersonal = companySettings.diligenceRequireNoPersonalLeave !== false; // default true

    // Evaluate criteria
    let isEligible = true;
    if (reqNoLate && lateMinutesTotal > 0) isEligible = false;
    if (reqNoLate && hasIncompleteWorkDay) isEligible = false; // Missing minutes (late or early leave) counts as a failure
    if (reqNoAbsence && absenceDaysCount > 0) isEligible = false;
    if (reqNoSick && sickLeaveCount > 0) isEligible = false;
    if (reqNoAnnual && annualLeaveCount > 0) isEligible = false;
    if (reqNoPersonal && personalLeaveCount > 0) isEligible = false;

    // Requirement: Month must be completed (no 'not_started' or 'resigned' days)
    if (isEligible) {
      if (notStartedCount > 0 || resignedCount > 0) {
        isEligible = false;
      }
    }

    if (isEligible) {
      let isMonthCompleted = false;
      const endOfMonthString = `${year}-${String(month).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`;
      if (todayString >= endOfMonthString) {
        isMonthCompleted = true;
      } else {
        let hasNotStarted = false;
        for (let d = 1; d <= daysInMonth; d++) {
          const dayData = processedDays[d] || {};
          if (!dayData.status || (!timesheet.isLocked && dayData.status === 'not_started')) {
            hasNotStarted = true;
            break;
          }
        }
        isMonthCompleted = !hasNotStarted;
      }
      if (!isMonthCompleted) isEligible = false;
    }

    if (isEligible && employee.type !== 'freelance') {
      diligenceAllowance = employeeDiligenceAmount;
    }
  }

  // Apply diligence allowance override if provided
  if (timesheet.overrides && timesheet.overrides.diligenceAllowance !== undefined) {
    diligenceAllowance = parseFloat(timesheet.overrides.diligenceAllowance || 0);
  }

  // 3. SECURITY DEPOSIT DEDUCTION (Requirement 5)
  // Security deposit is a one-time deduction made in the first month of working.
  let securityDepositDeducted = 0;
  const hasSecurityDepositOverride = timesheet.overrides && timesheet.overrides.securityDepositAmount !== undefined;

  if (hasSecurityDepositOverride) {
    securityDepositDeducted = parseFloat(timesheet.overrides.securityDepositAmount || 0);
  } else if (employee.requiresSecurityDeposit && employee.securityDepositStatus === 'none') {
    // If this is the start month or first payslip, deduct
    const startMonth = employee.startDate.slice(0, 7);
    if (timesheet.month === startMonth) {
      securityDepositDeducted = parseFloat(employee.securityDepositAmount || 0);
    }
  }

  // SECURITY DEPOSIT REFUND (Requirement 5)
  let securityDepositRefunded = 0;
  const hasSecurityRefundOverride = timesheet.overrides && timesheet.overrides.securityDepositRefundAmount !== undefined;

  if (hasSecurityRefundOverride) {
    securityDepositRefunded = parseFloat(timesheet.overrides.securityDepositRefundAmount || 0);
  } else if (employee.status === 'resigned' && employee.resignedDate && employee.requiresSecurityDeposit && employee.securityDepositStatus === 'deducted') {
    const resignationMonth = employee.resignedDate.slice(0, 7);
    if (timesheet.month === resignationMonth) {
      securityDepositRefunded = parseFloat(employee.securityDepositRefundAmount !== undefined ? employee.securityDepositRefundAmount : (employee.securityDepositAmount || 0));
    }
  }

  // 4. STUDENT LOAN DEDUCTION (กยศ) (Requirement 9)
  let studentLoanDeducted = 0;
  const hasStudentLoanOverride = timesheet.overrides && timesheet.overrides.studentLoanAmount !== undefined;

  if (hasStudentLoanOverride) {
    studentLoanDeducted = parseFloat(timesheet.overrides.studentLoanAmount || 0);
  } else if (employee.studentLoanFund && employee.studentLoanFund.enabled) {
    studentLoanDeducted = parseFloat(employee.studentLoanFund.monthlyAmount || 0);
  }

  // 5. OTHER INCOMES & OTHER DEDUCTIONS (Requirement 10)
  // Calculate from employee profile recurringItems + custom additions in this timesheet month
  let otherIncomes = 0;
  let otherDeductions = 0;
  const otherIncomeItems = [];
  const otherDeductionItems = [];

  // Apply profile recurring items
  if (employee.recurringItems) {
    employee.recurringItems.forEach(item => {
      // Skip Diligence item here because it's computed above conditionally
      if (item.name === 'เบี้ยขยัน') return;

      if (item.type === 'income') {
        otherIncomes += parseFloat(item.amount || 0);
        otherIncomeItems.push(item);
      } else {
        otherDeductions += parseFloat(item.amount || 0);
        otherDeductionItems.push(item);
      }
    });
  }

  // Add custom manual additions made directly inside this timesheet summary
  if (timesheet.manualItems) {
    timesheet.manualItems.forEach(item => {
      if (item.type === 'income') {
        otherIncomes += parseFloat(item.amount || 0);
        otherIncomeItems.push(item);
      } else {
        otherDeductions += parseFloat(item.amount || 0);
        otherDeductionItems.push(item);
      }
    });
  }

  // 6. SOCIAL SECURITY & WITHHOLDING TAX (Requirement 6 & 13 & 14)
  let socialSecurityDeducted = 0;
  let withholdingTaxDeducted = 0;

  // Gross taxable salary (base used for tax and net salary)
  // Note: Diligence allowance (เบี้ยขยัน) is EXCLUDED from SS (Requirement 7)
  const baseSalaryForSS = basicSalaryEarned - lateDeduction;

  const dedSettings = getEmployeeDeductionSettings(employee, companySettings);

  const hasSSOverride = overrideSSRate !== null && !isNaN(overrideSSRate);
  const effectiveSSRate = hasSSOverride ? overrideSSRate : dedSettings.socialSecurity.rate;
  const isSSEnabled = hasSSOverride ? true : dedSettings.socialSecurity.enabled;

  const hasTaxOverride = overrideTaxRate !== null && !isNaN(overrideTaxRate);
  const effectiveTaxRate = hasTaxOverride ? overrideTaxRate : dedSettings.withholdingTax.rate;
  const isTaxEnabled = hasTaxOverride ? true : dedSettings.withholdingTax.enabled;

  // Social Security Deduction
  if (isSSEnabled && effectiveSSRate > 0) {
    if (baseSalaryForSS > 0) {
      let rawSS = baseSalaryForSS * (effectiveSSRate / 100);

      // Retrieve contribution caps from settings
      let ssMinContribution = parseFloat(companySettings.socialSecurityMin || 82.5);
      let ssMaxContribution = parseFloat(companySettings.socialSecurityMax || 750);

      // Backward compatibility check: convert legacy salary bases (e.g. 1650 or 15000) to actual contribution caps
      if (ssMinContribution > 200) {
        ssMinContribution = Math.round(ssMinContribution * (effectiveSSRate / 100) * 100) / 100;
      }
      if (ssMaxContribution > 2000) {
        ssMaxContribution = Math.round(ssMaxContribution * (effectiveSSRate / 100) * 100) / 100;
      }

      // Cap the contribution amount directly
      if (rawSS < ssMinContribution) {
        rawSS = ssMinContribution;
      }
      if (rawSS > ssMaxContribution) {
        rawSS = ssMaxContribution;
      }
      socialSecurityDeducted = rawSS;
    }
  }

  // Withholding Tax Deduction
  const taxableIncome = Math.max(0, (employee.type === 'freelance')
    ? (basicSalaryEarned + diligenceAllowance + otherIncomes)
    : (basicSalaryEarned - lateDeduction + diligenceAllowance + otherIncomes));

  if (isTaxEnabled) {
    withholdingTaxDeducted = taxableIncome * (effectiveTaxRate / 100);
  }

  socialSecurityDeducted = Math.round(socialSecurityDeducted);
  withholdingTaxDeducted = Math.round(withholdingTaxDeducted * 100) / 100;

  // Apply direct amount overrides if provided (bypasses the integer rounding)
  if (timesheet.overrides && timesheet.overrides.socialSecurityAmount !== undefined) {
    socialSecurityDeducted = Math.round(parseFloat(timesheet.overrides.socialSecurityAmount));
  }
  if (timesheet.overrides && timesheet.overrides.withholdingTaxAmount !== undefined) {
    withholdingTaxDeducted = Math.round(parseFloat(timesheet.overrides.withholdingTaxAmount) * 100) / 100;
  }

  // 7. FINAL NET SALARY
  const totalGrossIncome = basicSalaryEarned + diligenceAllowance + otherIncomes + securityDepositRefunded;
  const totalDeductions = lateDeduction + securityDepositDeducted + studentLoanDeducted + socialSecurityDeducted + withholdingTaxDeducted + otherDeductions + totalAdvance;

  const netSalary = Math.round((totalGrossIncome - totalDeductions) * 100) / 100;

  return {
    basicSalaryEarned,
    taxableIncome,
    diligenceAllowance,
    lateDeduction,
    securityDepositDeducted,
    securityDepositRefunded,
    studentLoanDeducted,
    socialSecurityDeducted,
    withholdingTaxDeducted,
    otherIncomes,
    otherDeductions,
    otherIncomeItems,
    otherDeductionItems,
    totalAdvance,
    netSalary
  };
}

// Calculate the maximum allowed advance up to day D (Requirement 6)
export function calculateMaxAdvanceAllowed(employee, timesheet, companySettings, dayNumber, calendarState = null) {
  const state = getCalendarEvents(calendarState);
  const effectiveSalaryRate = timesheet.overrides && timesheet.overrides.salaryRate !== undefined ? parseFloat(timesheet.overrides.salaryRate) : (employee.salaryRate || 0);
  const [yearStr, monthStr] = timesheet.month.split('-');
  const year = parseInt(yearStr);
  const month = parseInt(monthStr);
  const daysInMonth = new Date(year, month, 0).getDate();

  let earnedDays = 0;
  let lateMinutes = 0;
  let absenceDays = 0;
  let sickDays = 0;
  let annualDays = 0;
  let personalDays = 0;
  let existingAdvanceTotal = 0;

  const now = new Date();
  const todayString = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const processedDays = {};

  for (let d = 1; d <= dayNumber; d++) {
    let dayData = timesheet.days[d];
    const dateString = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const dateObj = new Date(year, month - 1, d);

    // Detect default status based on calendars & start/resign dates
    let detectedStatus = 'work';
    const dayOfWeekNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayOfWeekNames[dateObj.getDay()];
    let isDefaultDayOff = false;
    if (employee.weeklyDaysOff !== undefined && employee.weeklyDaysOff.length > 0) {
      isDefaultDayOff = employee.weeklyDaysOff.includes(dateObj.getDay());
    } else {
      const workHoursStr = getHoursStr(employee, companySettings, dayName);
      isDefaultDayOff = workHoursStr === '0' || workHoursStr.startsWith('OFF:');
    }
    const isCompanyHoliday = state.holidays && state.holidays.some(h => h.date === dateString);
    const isSpecialWorkday = state.specialWorkdays && state.specialWorkdays.some(s => s.date === dateString);
    const isCompensatoryHoliday = state.specialWorkdays && state.specialWorkdays.some(s => s.compensatoryOffDate === dateString);

    if (employee.startDate && dateString < employee.startDate) detectedStatus = 'not_started';
    else if (employee.resignDate && dateString > employee.resignDate) detectedStatus = 'resigned';
    else if (dateString > todayString) detectedStatus = 'not_started';
    else if (isSpecialWorkday) detectedStatus = 'special_workday';
    else if (isCompensatoryHoliday) detectedStatus = 'compensatory_holiday';
    else if (isCompanyHoliday) detectedStatus = 'holiday';
    else if (isDefaultDayOff) detectedStatus = 'day_off';

    const isUntouched = dayData && !dayData.timeIn && !dayData.timeOut && !dayData.advance && (dayData.overrideAmount === undefined || dayData.overrideAmount === null || dayData.overrideAmount === '');

    if (!dayData) {
      dayData = { status: detectedStatus, advance: 0 };
    } else if ((isUntouched && dayData.status === 'work') || (!timesheet.isLocked && dayData.status === 'not_started')) {
      dayData = { ...dayData, status: detectedStatus };
    }

    processedDays[d] = dayData;
  }

  // Scan only up to the target day number
  for (let d = 1; d <= dayNumber; d++) {
    const dayData = processedDays[d] || { status: 'work', lateMinutes: 0, earlyLeaveMinutes: 0, advance: 0 };

    // Add existing advances excluding the target day itself if we are editing
    if (d !== dayNumber) {
      existingAdvanceTotal += parseFloat(dayData.advance || 0);
    }

    if (dayData.status === 'work') {
      earnedDays++;
      // late/early minutes are calculated later inside the deduction loop, just like in calculatePayrollForEmployee
    } else if (dayData.status === 'absence' || dayData.status === 'not_started' || dayData.status === 'resigned') {
      absenceDays++;
    } else if (dayData.status === 'leave_sick') {
      sickDays++;
    } else if (dayData.status === 'leave_annual') {
      annualDays++;
    } else if (dayData.status === 'leave_personal') {
      personalDays++;
    }
  }

  // Earned income base up to day D
  let earnedIncome = 0;
  if (employee.type === 'salary') {
    const dailyRate = effectiveSalaryRate / 30;
    // Base salary earned up to this day D (cap elapsed days at 30 for calculation)
    const elapsedDays = Math.min(dayNumber, 30);
    // Prorate salary by number of elapsed calendar days in the month (using 30-day rule)
    earnedIncome = (effectiveSalaryRate / 30) * elapsedDays;

    // Deduct unpaid absences and leaves up to today
    earnedIncome -= (absenceDays * dailyRate);
    if (!companySettings.enableSickLeaveCalc) {
      earnedIncome -= (sickDays * dailyRate);
    }
    if (!companySettings.enableAnnualLeaveCalc) {
      earnedIncome -= (annualDays * dailyRate);
    }
      if (!companySettings.enablePersonalLeaveCalc) {
        earnedIncome -= (personalDays * dailyRate);
      }

  } else if (employee.type === 'daily' || employee.type === 'freelance') {
    for (let d = 1; d <= dayNumber; d++) {
      const dayData = processedDays[d] || {};
      let isPaid = false;
      if (dayData.status === 'work' || dayData.status === 'holiday_selected' || dayData.status === 'special_workday' || dayData.status === 'holiday') {
        isPaid = true;
      } else if (employee.type === 'daily') {
        if (companySettings.enableSickLeaveCalc && dayData.status === 'leave_sick') isPaid = true;
        if (companySettings.enableAnnualLeaveCalc && dayData.status === 'leave_annual') isPaid = true;
      }
      if (isPaid) {
        if (dayData.overrideAmount !== undefined && dayData.overrideAmount !== null && dayData.overrideAmount !== '') {
          earnedIncome += parseFloat(dayData.overrideAmount);
        } else {
          earnedIncome += effectiveSalaryRate;
        }
      }
    }
  }

  // Subtract late/early fee accrued up to today
  let lateFee = 0;
  if (employee.type !== 'freelance') {
    for (let d = 1; d <= dayNumber; d++) {
      const dayData = processedDays[d] || {};
      if (dayData.status === 'work') {
        const dateObj = new Date(year, month - 1, d);
        const dayOfWeekNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const dayName = dayOfWeekNames[dateObj.getDay()];
        const workHoursStr = getHoursStr(employee, companySettings, dayName);
        let workHours = parseWorkHoursRange(workHoursStr);

        const dateString = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        const specialDay = state.specialWorkdays && state.specialWorkdays.find(s => s.date === dateString);

        if (workHours === 0) {
          for (const fallbackDay of dayOfWeekNames) {
            const fallbackStr = getHoursStr(employee, companySettings, fallbackDay);
            const fallbackHours = parseWorkHoursRange(fallbackStr);
            if (fallbackHours > 0) {
              workHours = fallbackHours;
              break;
            }
          }
          if (workHours === 0) workHours = 8;
        }

        if (workHours > 0) {
          const dailyRate = employee.type === 'salary' ? (effectiveSalaryRate / 30) : effectiveSalaryRate;
          const minuteRate = dailyRate / workHours / 60;

          let dayLateMins = parseInt(dayData.lateMinutes || 0);
          let dayEarlyMins = parseInt(dayData.earlyLeaveMinutes || 0);

          if (workHoursStr.includes('-')) {
            const [startStr, endStr] = workHoursStr.split('-');
            if (dayData.timeIn) {
              const [inH, inM] = dayData.timeIn.split(':').map(Number);
              const [startH, startM] = startStr.split(':').map(Number);
              if (!isNaN(inH) && !isNaN(inM) && !isNaN(startH) && !isNaN(startM)) {
                const inTotal = inH * 60 + inM;
                const startTotal = startH * 60 + startM;
                if (inTotal > startTotal) dayLateMins = inTotal - startTotal;
                else dayLateMins = 0;
              }
            }
            if (dayData.timeOut) {
              const [outH, outM] = dayData.timeOut.split(':').map(Number);
              const [endH, endM] = endStr.split(':').map(Number);
              if (!isNaN(outH) && !isNaN(outM) && !isNaN(endH) && !isNaN(endM)) {
                const outTotal = outH * 60 + outM;
                const endTotal = endH * 60 + endM;
                if (outTotal < endTotal) dayEarlyMins = endTotal - outTotal;
                else dayEarlyMins = 0;
              }
            }
          }

          lateFee += (dayLateMins + dayEarlyMins) * minuteRate;
        }
      }
    }
  }

  // Deduct full student loan (กยศ)
  let studentLoanPr = 0;
  if (employee.studentLoanFund && employee.studentLoanFund.enabled) {
    studentLoanPr = parseFloat(employee.studentLoanFund.monthlyAmount) || 0;
  }

  let otherIncomes = 0;
  let otherDeductions = 0;
  if (employee.recurringItems) {
    employee.recurringItems.forEach(item => {
      if (item.name === 'เบี้ยขยัน') return;
      if (item.type === 'income') otherIncomes += parseFloat(item.amount || 0);
      else otherDeductions += parseFloat(item.amount || 0);
    });
  }
  if (timesheet && timesheet.manualItems) {
    timesheet.manualItems.forEach(item => {
      if (item.type === 'income') otherIncomes += parseFloat(item.amount || 0);
      else otherDeductions += parseFloat(item.amount || 0);
    });
  }

  const dedSettings = getEmployeeDeductionSettings(employee, companySettings);
  const effectiveSSRate = timesheet && timesheet.overrides && timesheet.overrides.socialSecurityRate !== undefined ? parseFloat(timesheet.overrides.socialSecurityRate) : dedSettings.socialSecurity.rate;
  const effectiveTaxRate = timesheet && timesheet.overrides && timesheet.overrides.withholdingTaxRate !== undefined ? parseFloat(timesheet.overrides.withholdingTaxRate) : dedSettings.withholdingTax.rate;

  // Subtract pro-rated social security up to this point
  let ssPr = 0;
  if (dedSettings.socialSecurity.enabled) {
    const baseSS = earnedIncome - lateFee;
    if (baseSS > 0) {
      let rawSS = baseSS * (effectiveSSRate / 100);

      // Retrieve contribution caps
      let ssMinContribution = parseFloat(companySettings.socialSecurityMin || 82.5);
      let ssMaxContribution = parseFloat(companySettings.socialSecurityMax || 750);

      // Backward compatibility check
      if (ssMinContribution > 200) {
        ssMinContribution = Math.round(ssMinContribution * (effectiveSSRate / 100) * 100) / 100;
      }
      if (ssMaxContribution > 2000) {
        ssMaxContribution = Math.round(ssMaxContribution * (effectiveSSRate / 100) * 100) / 100;
      }

      // Pro-rate caps
      const capElapsedDays = Math.min(dayNumber, 30);
      const minCapPr = (ssMinContribution / 30) * capElapsedDays;
      const maxCapPr = (ssMaxContribution / 30) * capElapsedDays;

      if (rawSS < minCapPr) rawSS = minCapPr;
      if (rawSS > maxCapPr) rawSS = maxCapPr;
      ssPr = rawSS;
    }
  }

  // Subtract withholding tax up to this point
  let taxPr = 0;
  if (dedSettings.withholdingTax.enabled) {
    const taxableIncome = (employee.type === 'freelance') ? (earnedIncome + otherIncomes) : (earnedIncome - lateFee + otherIncomes);
    taxPr = taxableIncome * (effectiveTaxRate / 100);
  }

  const maxAvailable = Math.round((earnedIncome + otherIncomes - lateFee - studentLoanPr - otherDeductions - ssPr - taxPr - existingAdvanceTotal) * 100) / 100;

  return maxAvailable < 0 ? 0 : maxAvailable;
}

export function finalizeTimesheetDays(emp, ts, month, companySettings = null, calendarState = null) {
  const state = getCalendarEvents(calendarState);
  const [yearStr, monthStr] = month.split('-');
  const year = parseInt(yearStr);
  const m = parseInt(monthStr);
  const daysInMonth = new Date(year, m, 0).getDate();

  if (!ts.days) ts.days = {};

  const dayOfWeekNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const currentMonthStr = `${currentYear}-${String(currentMonth).padStart(2, '0')}`;
  const todayDate = now.getDate();
  const todayString = `${currentMonthStr}-${String(todayDate).padStart(2, '0')}`;

  for (let d = 1; d <= daysInMonth; d++) {
    const dateObj = new Date(year, m - 1, d);
    const dateString = `${year}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const dayName = dayOfWeekNames[dateObj.getDay()];

    let isDefaultDayOff = false;
    if (emp.weeklyDaysOff !== undefined && emp.weeklyDaysOff.length > 0) {
      isDefaultDayOff = emp.weeklyDaysOff.includes(dateObj.getDay());
    } else {
      const workHoursStr = getHoursStr(emp, state.activeCompanySettings, dayName);
      isDefaultDayOff = workHoursStr === '0' || workHoursStr.startsWith('OFF:');
    }

    const isCompanyHoliday = state.holidays && state.holidays.some(h => h.date === dateString);
    const isSpecialWorkday = state.specialWorkdays && state.specialWorkdays.some(s => s.date === dateString);
    const isCompensatoryHoliday = state.specialWorkdays && state.specialWorkdays.some(s => s.compensatoryOffDate === dateString);

    let detectedStatus = 'work';
    if (emp.startDate && dateString < emp.startDate) detectedStatus = 'not_started';
    else if (emp.resignDate && dateString > emp.resignDate) detectedStatus = 'resigned';
    else if (dateString > todayString) detectedStatus = 'not_started';
    else if (isSpecialWorkday) detectedStatus = 'special_workday';
    else if (isCompensatoryHoliday) detectedStatus = 'compensatory_holiday';
    else if (isCompanyHoliday) detectedStatus = 'holiday';
    else if (isDefaultDayOff) detectedStatus = 'day_off';

    if (!ts.days[d]) {
      ts.days[d] = {
        status: detectedStatus,
        lateMinutes: 0,
        earlyLeaveMinutes: 0,
        advance: 0
      };
    } else {
      const dayData = ts.days[d];
      const isUntouched = !dayData.timeIn && !dayData.timeOut && !dayData.advance && (dayData.overrideAmount === undefined || dayData.overrideAmount === null || dayData.overrideAmount === '');

      if (dateString > todayString) {
        if (isUntouched && dayData.status !== 'resigned') {
          dayData.status = 'not_started';
        }
      } else {
        if (dayData.status === 'not_started' && !(emp.startDate && dateString < emp.startDate)) {
          dayData.status = detectedStatus;
        } else if (isUntouched && dayData.status === 'work' && detectedStatus !== 'work') {
          dayData.status = detectedStatus;
        }
      }
    }
  }
}


export function convertToThaiBahtText(num) {
  if (isNaN(num) || num === null) return '';
  const thNames = ['', 'หนึ่ง', 'สอง', 'สาม', 'สี่', 'ห้า', 'หก', 'เจ็ด', 'แปด', 'เก้า'];
  const thUnits = ['', 'สิบ', 'ร้อย', 'พัน', 'หมื่น', 'แสน', 'ล้าน'];

  const totalSatangs = Math.round(num * 100);
  const baht = Math.floor(totalSatangs / 100);
  const satang = totalSatangs % 100;

  if (baht === 0 && satang === 0) return 'ศูนย์บาทถ้วน';

  let bahtText = '';
  const bahtDigits = baht.toString();
  const len = bahtDigits.length;

  for (let i = 0; i < len; i++) {
    const d = parseInt(bahtDigits[i]);
    if (d !== 0) {
      let unitText = thUnits[len - 1 - i];
      let digitText = thNames[d];

      if (len - 1 - i === 1) {
        if (d === 1) digitText = '';
        else if (d === 2) digitText = 'ยี่';
      }
      if (len - 1 - i === 0 && len > 1 && d === 1) {
        digitText = 'เอ็ด';
      }

      bahtText += digitText + unitText;
    }
  }

  if (baht > 0) bahtText += 'บาท';

  if (satang === 0) {
    bahtText += 'ถ้วน';
  } else {
    let satangText = '';
    const satangDigits = satang.toString().padStart(2, '0');
    const d1 = parseInt(satangDigits[0]);
    const d2 = parseInt(satangDigits[1]);

    if (d1 !== 0) {
      let digitText = thNames[d1];
      if (d1 === 1) digitText = '';
      else if (d1 === 2) digitText = 'ยี่';
      satangText += digitText + 'สิบ';
    }
    if (d2 !== 0) {
      let digitText = thNames[d2];
      if (d2 === 1 && d1 !== 0) digitText = 'เอ็ด';
      satangText += digitText;
    }
    bahtText += satangText + 'สตางค์';
  }

  return bahtText;
}


// Bind to window for global access and backward compatibility
if (typeof window !== 'undefined') {
  window.calculatePayrollForEmployee = calculatePayrollForEmployee;
  window.calculateMaxAdvanceAllowed = calculateMaxAdvanceAllowed;
  window.finalizeTimesheetDays = finalizeTimesheetDays;
  window.convertToThaiBahtText = convertToThaiBahtText;
  window.parseWorkHoursRange = parseWorkHoursRange;
  window.getEmployeeDeductionSettings = getEmployeeDeductionSettings;
  window.isEmployeeActiveInMonth = isEmployeeActiveInMonth;
  window.getEmployeeWorkHoursStr = getEmployeeWorkHoursStr;
}
